import { Icons } from '../assets/icons'
import {
    Assignation,
    FSM_ACTIONS_TYPE,
    BUTTON_TYPE,
    RANKING,
    CommonValueType,
    AsgnState,
    AsgnStateActions,
} from './types'
import _ from 'lodash'
import { getTranslator, langIndex2AppLang } from '../i18n/useTranslation'
import { useEffect } from 'react'
import { getFlags } from '../components/progress/useProgress'
import { diffSeconds } from '../utils'
import { apiService } from '../services/apiServices'
import { AssessmentDTO, NO_SCORE } from '../services/types'
import { VALUES } from '../utils/values/cats'
import { DEFAULT_AUTH_STATE, FSM_STATES } from './defaultState'
import { info, logException } from '../services/sentryLogger'
import { buckets2str, rounds2str, stms2str } from '../utils/stm'
import { getNextScreen } from './fsmUtils'
import { PartialParameters } from '../parameters/types'
import { SessionData } from './session/types'
import { loadLocalSession, resetSession } from './session/helpers'
import { restoreSession } from './session/converter'
import { STM } from './types'
import { TKeys } from '../i18n/types'

// FIXME hooks; at each render
export const useFSM = (
    args: {
        params: PartialParameters
        resetFn: any
        hasAlert?: boolean
        loginData: typeof DEFAULT_AUTH_STATE
    } & AsgnState &
        AsgnStateActions
): FSM_ACTIONS_TYPE => {
    let {
        state,
        setState,
        rounds,
        setRounds,
        asgnsForRound,
        setAsgnsForRound,
        sessionData,
        asgnsLeft2Rank,
        asgnsRanked,
        items2compare,
        setItems2compare,
        items2rank,
        setItems2rank,
        setSessionData,
        setAndSaveSessionData,
        hasAlert = false, // DEFAULT 
        params: readOnlyParams, //FIXME
        loginData,
        allStms = {} as Record<STM['preferredType'], Assignation[]>,
        scannedItems,
    } = args

    const params = {
        ...readOnlyParams,
        ...state.params,
    }
    const { fsmState, currentStmIndex, currentRound, startedAt } = state
    const nRounds = params.assessmentParams['Nb of rounds']
    const langIndex = params.assessmentParams['langIndex']
    const is360 = params?.assessmentParams?.is360
    const current360Type: STM['preferredType'] =
        fsmState === FSM_STATES.PICKER_LEADERSHIP_360 ? 'leadership' : 'workenv'
    const t = getTranslator(langIndex)
    info('useFSM', { fsmState, loginData, params, startedAt })

    const { isConfirming, howTos, isStateWithoutProfile } = getFlags(fsmState)
    const defaultOutput: FSM_ACTIONS_TYPE = {
        hasTopTitle: true,
        topTitle: '',
        topSubtitle: '',
        bottomLeft: {
            color: 'inherit',
            variant: 'contained', 
            label: t('btn_back'),
        } as BUTTON_TYPE,
        bottomRight: {
            color: 'primary',
            variant: 'contained',
            label: t('btn_next'),
        } as BUTTON_TYPE,
        onSortRoundComplete: () => {},
        hasProgress: !isConfirming && !howTos,
        hasBottomNav: true,
        isRunning: true,
    }

    useEffect(() => {
        if (getFlags(fsmState)?.isStateWSortableList) {
            info(
                'Preparing items2rank',
                '= asgnsRanked.current =',
                stms2str(asgnsRanked.current)
            )
            setItems2rank([...asgnsRanked.current])
        }
    }, [fsmState])

    useEffect(() => {
        // FIXME remove - for monitoring purpose (Sentry)
        if (fsmState === FSM_STATES.RANK_14_X_BY_X) {
            info('useEffectX_X', rounds2str(rounds))
        }
    }, [fsmState, rounds])

    const isFirstStm = currentStmIndex === 0
    console.debug('DEBUG', { is360, fsmState, current360Type })
    const isLastStm =
        currentStmIndex ===
        (is360 ? allStms[current360Type] : asgnsForRound).length - 1
    const isLastRound = currentRound === nRounds

    // FIXME use memo
    let nextState = getNextScreen(fsmState, params, isLastStm, isLastRound)

    info('before sw', { fsmState, nextState })

    // When the rank has been confirmed (re-sorted) at the end of a round
    const onRankRoundComplete = (rankedItems: readonly Assignation[]) => {
        const localNextState = getNextScreen(
            fsmState,
            params,
            true,
            isLastRound
        )
        info(
            '>>>>>>>>>',
            isLastRound
                ? 'CONFIRM LAST ROUND clicked onRankRoundComplete, will updateRounds() then HOW_TO_BUCKETS'
                : 'NEXT ROUND clicked onRankRoundComplete',
            { fsmState, currentRound, isLastRound, localNextState }
        )
        const ordered2insert = rankedItems.slice()
        setRounds((prevRounds: readonly Assignation[][]) => {
            info(
                'updateRoundsA',
                fsmState,
                currentRound,
                'rankedItems=',
                stms2str(rankedItems), // FIXME error here
                'prevRounds=',
                rounds2str(prevRounds)
            )
            const newRounds = [...prevRounds]
            newRounds[currentRound - 1] = ordered2insert
            info(
                'updateRoundsB',
                fsmState,
                'ordered2insert',
                stms2str(ordered2insert),
                'newRounds',
                rounds2str(newRounds)
            )
            return newRounds
        })
        if (!isLastRound) {
            const nextRound = currentRound + 1
            const newAssignations = rounds[nextRound - 1].slice()
            info(
                'RANK_FOR_ROUND',
                nextRound,
                'rounds',
                rounds2str(rounds), // FIXME old value of round
                'newAssignations!',
                stms2str(newAssignations)
            )
            setAsgnsForRound(newAssignations)
            asgnsLeft2Rank.current = newAssignations.slice() // FIXME bug
            asgnsRanked.current = []
        }
        info('prevState', { fsmState, localNextState })
        setState((s) => ({
            ...s,
            currentStmIndex: 0,
            currentBucketRanking: 0,
            currentRound: isLastRound ? s.currentRound : s.currentRound + 1,
            fsmState: localNextState,
        }))
        setAndSaveSessionData((sd: SessionData) => ({
            ...sd,
            restorable: {
                ...sd.restorable,
                fsmState: localNextState,
                currentRoundIndex: isLastRound
                    ? sd.restorable.currentRoundIndex
                    : sd.restorable.currentRoundIndex + 1,
                //
                savedRounds: sd.restorable.savedRounds.map((round, i) =>
                    i === sd.restorable.currentRoundIndex
                        ? {
                              ...round,
                              partiallyRankedRound: ordered2insert.map(
                                  (a) => a.valueKey
                              ) as string[],
                          }
                        : round
                ),
            },
        }))
    }

    // At each render:
    switch (fsmState) {
        case FSM_STATES['AUTH']:
            return {
                ...defaultOutput,
                hasTopTitle: false,
                hasBottomNav: false,
                hasProgress: false,
                isRunning: false,
            }
        case FSM_STATES['INTRO']:
            return {
                ...defaultOutput,
                hasProgress: false,
                isRunning: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    onClick: () =>
                        setState((s) => ({
                            ...s,
                            fsmState: FSM_STATES['AUTH'],
                        })),
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        info(fsmState, 'clicked')
                        setState((s) => ({
                            ...s,
                            startedAt: new Date(), // FIXME repeated
                            fsmState: nextState,
                        }))
                    },
                },
            }
        case FSM_STATES['BRANDING']:
            return {
                ...defaultOutput,
                hasProgress: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    onClick: () =>
                        setState((s) => ({
                            ...s,
                            fsmState: getNextScreen(
                                FSM_STATES.AUTH, // TODO here BRANDING ??
                                params,
                                isLastStm,
                                isLastRound
                            ),
                        })),
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    label: t('btn_next'),
                    onClick: () => {
                        info(fsmState, 'clicked')
                        setState((s) => ({
                            ...s,
                            startedAt: new Date(), // FIXME repeated
                            fsmState: nextState,
                        }))
                    },
                },
            }
        case FSM_STATES['QSORT_V2_RANKER']:
            // TODO to test thoroughly
            return {
                ...defaultOutput,
                hasTopTitle: false,
                hasBottomNav: false,
                hasProgress: false,
                isRunning: true,
            }
        case FSM_STATES['HOW_TO_Q_SORT']:
        case FSM_STATES['HOW_TO_BINARY']:
            return {
                ...defaultOutput,
                topTitle: '',
                topSubtitle: '',
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    onClick: () => {
                        const previousState = getNextScreen(
                            FSM_STATES.INTRO,
                            params,
                            isLastStm,
                            isLastRound
                        )
                        setState((s) => ({
                            ...s,
                            fsmState: previousState,
                        }))
                    },
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    label: t('btn_start'),
                    onClick: () => {
                        info(fsmState, 'clicked')
                        setState((s) => ({
                            ...s,
                            fsmState: nextState,
                            startedAt: new Date(), // FIXME repeated
                        }))
                    },
                    color: 'primary',
                },
                hasProgress: false,
                isRunning: false,
            }
        case FSM_STATES['RANK_14_X_BY_X']:
            // FIXME each team we come back here, this code is re-executed
            // (from profile)
            const newOutput = _.cloneDeep(defaultOutput)
            newOutput.hasBottomNav = false
            newOutput.isRunning = true // FIXME refactor bug
            newOutput.topLeft = {
                color: 'inherit',
                label: '',
                variant: 'text',
                // Icon: Icons.Undo,
                Icon: Icons.Back,
                onClick: () => {
                    const sessionData = loadLocalSession()
                    if ((!hasAlert || window.confirm(t('hint_undo'))) && sessionData) {
                        info('UNDO clicked', { currentStmIndex, sessionData })
                        restoreSession(
                            sessionData,
                            args as unknown as CommonValueType, // FIXME
                            loginData
                        )
                    }
                },
            }
            newOutput.topCenter = isStateWithoutProfile ? undefined : {
                disabled: isConfirming,
                variant: 'outlined',
                label: t('my_profile'),
                // Icon: Icons.MenuIcon,
                Icon: Icons.Edit,
                color: 'inherit',
                onClick: () => {
                    info(fsmState, 'Profile clicked')
                    setState((s: any) => ({
                        ...s,
                        fsmState: nextState,
                        // fsmState: FSM_STATES['RANK_FOR_PROFILE'],
                    }))
                }
            }
            if (isFirstStm) {
                newOutput.topLeft.onClick = console.log // FIXME
            }

            if (isLastStm) {
                // FIXME bottom center !
                newOutput.bottomRight.label = t('btn_next_round')
                newOutput.bottomRight.variant = 'contained'
                newOutput.bottomRight.color = 'primary'
                newOutput.bottomRight.Icon = Icons.Next
                newOutput.bottomRight.onClick = () => {
                    info(fsmState, 'clicked', 'Validate ROUND ?', { isLastStm })
                    setState((s) => ({
                        ...s,
                        fsmState: nextState,
                    }))
                }
            } 
            // After binaries of a round, before ranking confirmation
            newOutput.onSortRoundComplete = (
                sortedArray: readonly Assignation[]
            ) => {
                const roundName = `round${currentRound}`
                info(
                    '>>>>>>>>>>>>>>> onSortRoundComplete',
                    fsmState,
                    roundName,
                    'sortedArray=',
                    stms2str(sortedArray),
                    'asgnsRanked.current=',
                    stms2str(asgnsRanked.current) // the lowest index in the array, the best
                )
                const nextAssignations = sortedArray.map((a, i) => ({
                    ...a,
                    index: i + 1, // FIXME unused 'index' prop ? overroden with new 'ranking', but not in the asgnsRanked.current REF
                }))
                // FIXME still necessary? Will change index like a temp ranking, remove weight, and add fake bucket
                setAsgnsForRound((prevAssignations) => {
                    info(
                        'onSortRoundComplete',
                        roundName,
                        'prev=',
                        stms2str(prevAssignations)
                    )
                    // const next = sortAndExtend(prev, sortedArray, 0, lang)
                    // FIXME toReversed() ?
                    // FIXME AsgnsForRound used ?

                    info(
                        'onSortRoundComplete',
                        roundName,
                        'nextAssignations',
                        stms2str(nextAssignations)
                    )
                    return nextAssignations // FIXME might not be necessary for last step
                })
                const localNextState = getNextScreen(
                    fsmState,
                    params,
                    true,
                    isLastRound
                )
                info('onSortRoundComplete?', { fsmState, next: localNextState })

                // TODO nextAssignations
                asgnsRanked.current = nextAssignations.slice()
                // New round after the ranking
                if (localNextState === FSM_STATES['RANK_14_X_BY_X']) {
                    console.log('expected=', stms2str(asgnsRanked.current))
                    onRankRoundComplete([...asgnsRanked.current]) //FIXME cannot set state in this render run
                } else {
                    // Confirm round ranking
                    setState((s) => ({
                        ...s,
                        fsmState: localNextState,
                    }))
                    setAndSaveSessionData((sd: SessionData) => ({
                        ...sd,
                        // [`${roundName}Result`]: _.cloneDeep(sortedArray), // FIXME unused vs asgnsRanked.current?
                        [`${roundName}Result`]: _.cloneDeep(
                            asgnsRanked.current
                        ),
                        restorable: {
                            ...sd.restorable,
                            fsmState: localNextState, //FIXME only change here
                        },
                    }))
                }
            }
            return newOutput
        case FSM_STATES['AWARD_ROUND']:
            let bottomRightOption = {
                ...defaultOutput.bottomRight,
                onClick: () => {
                    info(fsmState, 'clicked', nextState)

                    setState((s) => ({
                        ...s,
                        fsmState: nextState,
                    }))
                },
                color: 'primary' as BUTTON_TYPE['color'],
            }
            if (!params?.assessmentParams?.hasConfirmRound) {
                // FIXME dup code; if we skip, we have the right button
                bottomRightOption = {
                    ...bottomRightOption,
                    variant: 'contained',
                    label: isLastRound ? t('btn_confirm') : t('btn_next_round'),
                    color: 'primary',
                    Icon: Icons.Next,
                    // FIXME asgnsRanked.current (and not empty items2rank when skipped )
                    onClick: () =>
                        onRankRoundComplete([...asgnsRanked.current]),
                }
            }
            return {
                ...defaultOutput,
                bottomRight: {
                    ...bottomRightOption,
                },
            }
        case FSM_STATES['RANK_FOR_ROUND']:
            info('RANK_FOR_ROUND case')
            return {
                ...defaultOutput,
                bottomRight: {
                    variant: 'contained',
                    label: isLastRound ? t('btn_confirm') : t('btn_next_round'),
                    color: 'primary',
                    Icon: Icons.Next,
                    onClick: () => onRankRoundComplete(items2rank),
                },
            }
        case FSM_STATES['RANK_FOR_PROFILE']:
            // FIXME has back()
            return {
                ...defaultOutput,
                topTitle: t('hint_profile'),
                // topLeft: {
                //     color: 'inherit',
                //     label: '',
                //     variant: 'text',
                //     Icon: Icons.Back,
                //     onClick: () => {
                //         info(fsmState, 'cancel clicked')
                //         if (
                //             hasAlert
                //         ) {
                //             confirm(t('tip_quit') + '\n\n' + t('hint_undo')) && setState((s) => ({
                //                 ...s,
                //                 fsmState: FSM_STATES['RANK_14_X_BY_X'],
                //             }))
                //         } else {
                //             setState((s) => ({
                //                 ...s,
                //                 fsmState: FSM_STATES['RANK_14_X_BY_X'],
                //             }))
                //         }
                //     },
                // },
                bottomRight: {
                    variant: 'contained',
                    label: t('btn_confirm'),
                    color: 'primary',
                    Icon: Icons.Next,
                    onClick: () => {
                        info(fsmState, 'clicked PROFILE OK')
                        const newAssignations = [...items2rank]
                        info(
                            'RANK_FOR_PROFILE confirm clicked',
                            'newAssignations',
                            stms2str(newAssignations)
                        )
                        asgnsRanked.current = newAssignations.slice()
                        setAndSaveSessionData((sd: SessionData) => ({
                            ...sd,
                            restorable: {
                                ...sd.restorable,
                                savedRounds: sd.restorable.savedRounds.map(
                                    (r, i) =>
                                        sd.restorable.currentRoundIndex === i
                                            ? {
                                                  ...r,
                                                  partiallyRankedRound:
                                                      //   was newAssignations
                                                      asgnsRanked.current.map(
                                                          (a) => a.valueKey
                                                      ),
                                              }
                                            : r
                                ),
                            },
                        }))
                        setState((s) => ({
                            ...s,
                            fsmState: FSM_STATES['RANK_14_X_BY_X'],
                        }))
                    },
                },
                bottomLeft: {
                    label: t('btn_cancel'),
                    // Icon: Icons.Back,
                    color: 'inherit',
                    onClick: () => {
                        info(fsmState, 'cancel clicked')
                        if (
                            hasAlert
                        ) {
                            confirm(t('tip_quit') + '\n\n' + t('hint_undo')) && setState((s) => ({
                                ...s,
                                fsmState: FSM_STATES['RANK_14_X_BY_X'],
                            }))
                        } else {
                            setState((s) => ({
                                ...s,
                                fsmState: FSM_STATES['RANK_14_X_BY_X'],
                            }))
                        }
                    },
                },
            }
        case FSM_STATES['HOW_TO_BUCKETS']:
            return {
                ...defaultOutput,
                topSubtitle: '',
                bottomLeft: {
                    label: t('btn_to_params'),
                    Icon: Icons.Back,
                    color: 'inherit',
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        info(fsmState, 'clicked')
                        setState((s) => ({
                            ...s,
                            fsmState: nextState,
                        }))
                    },
                    color: 'primary',
                },
            }
        case FSM_STATES['RANK_FOR_BUCKET']:
            return {
                ...defaultOutput,
                hasBottomNav: false, // CUSTOM
                hasProgress: true,
                onRankBucketComplete: (buckets) => {
                    // FIXME prettier this
                    info('>>>>>>>> onRankBucketComplete', buckets2str(buckets))
                    // FINAL RESULT
                    const ranking = _.flattenDeep(buckets)
                    setAsgnsForRound(ranking) // FIXME
                    setSessionData((p: SessionData) => ({
                        ...p,
                        timeSec: diffSeconds(startedAt),
                        ranking,
                    }))
                    const assessment = ranking.reduce<AssessmentDTO>(
                        (acc, current) => {
                            const value = current.valueKey as VALUES
                            acc[value] = (current.bucket as RANKING) ?? 1 // FIXME
                            return acc
                        },
                        {} as AssessmentDTO
                    )
                    const today = new Date()
                    // (1) Publish the assessment from UI v2
                    apiService
                        .publishAssessment({
                            ...assessment,
                            //
                            slug: loginData.slug,
                            source: loginData.source,
                            from: loginData.from,
                            //
                            startDate: startedAt.toISOString(),
                            endDate: today.toISOString(),
                            durationSeconds:
                                sessionData.restorable.durationAtSave +
                                diffSeconds(
                                    new Date(
                                        sessionData.restorable.savedDateStr
                                    )
                                ),
                        }
                        , params.assessmentParams.isQSortV2
                    )
                        .then(([code, subcode]) => {
                            info('publishAssessment', code)
                            if (code === 'OK') {
                                info(fsmState, 'clicked')
                                resetSession()
                                setState((s) => ({
                                    ...s,
                                    currentBucketRanking:
                                        s.currentBucketRanking + 1,
                                    overrideTitle: '',
                                    overrideSubTitle: '',
                                    error: null,
                                    fsmState: nextState,
                                }))
                            } else {
                                setState((s) => ({
                                    ...s,
                                    currentBucketRanking:
                                        s.currentBucketRanking + 1,
                                    overrideTitle: '',
                                    overrideSubTitle: '',
                                    fsmState: FSM_STATES.ERROR,
                                    error: [code, subcode],
                                }))
                            }
                        })
                        .catch((e) =>
                            logException('publishAssessment', 'useFSM', e)
                        )
                },
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                },
            }
        case FSM_STATES['FINAL_OVERVIEW']:
            return {
                ...defaultOutput,
                hasBottomNav: true,
                hasProgress: false,
                isRunning: false,
                topTitle: t('title_thanks'),
                topSubtitle: '',
                bottomLeft: {
                    label: `${t('btn_to_bucket_x')} ${state.currentBucket}`,
                    Icon: Icons.Back,
                    color: 'inherit',
                    // onClick: () => {
                    //     setState((s) => ({
                    //         ...s,
                    //         fsmState: getNextScreen('HOW_TO_BUCKETS', params, true, true),
                    //         currentBucket: state.params.assessmentParams['Buckets'] - 1,
                    //     }))
                    // },
                },
            }
        case FSM_STATES['ERROR']:
            return {
                // FIXME? nav buttons?
                ...defaultOutput,
                hasBottomNav: true,
                hasProgress: false,
                isRunning: false,
                topTitle: t('title_error'),
                topSubtitle: '',
            }
        // FIXME externalize
        case FSM_STATES.HOW_TO_360:
            return {
                ...defaultOutput,
                hasProgress: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    disabled: true,
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        setState((s) => ({
                            ...s,
                            fsmState: nextState,
                        }))
                    },
                },
            }
        case FSM_STATES.PICKER_LEADERSHIP_360:
        case FSM_STATES.PICKER_WORKENV_360:
        case FSM_STATES.PICKER_360:
            let currentStms = allStms[current360Type]
            let currentStm = currentStms[state.currentStmIndex]
            const currentItem = scannedItems.find(
                (s) => s.RefQuestion === currentStm?.valueKey
            )
            const currentScore = currentItem?.Score
            return {
                ...defaultOutput,
                topTitle: t(
                    `picker_360_title_${current360Type}` as TKeys
                ),
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    disabled: state.currentStmIndex === 0,
                    onClick: () =>
                        setState((s) => ({
                            ...s,
                            currentStmIndex: Math.max(s.currentStmIndex - 1, 0),
                        })),
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    disabled: currentScore === NO_SCORE,
                    onClick: () => {
                        console.debug(
                            'isLastStm',
                            state.currentStmIndex,
                            isLastStm,
                            currentStms
                        )
                        setState((s) => ({
                            ...s,
                            fsmState: nextState,
                            currentStmIndex: Math.min(
                                s.currentStmIndex + 1,
                                currentStms.length - 1
                            ),
                        }))
                    },
                },
            }
        case FSM_STATES.HOW_TO_WORKENV:
            return {
                ...defaultOutput,
                topTitle: t(`picker_360_title_workenv` as TKeys),
                hasProgress: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    disabled: true,
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        setState((s) => ({
                            ...s,
                            currentStmIndex: 0,
                            fsmState: nextState,
                        }))
                    },
                },
            }
        case FSM_STATES.HOW_TO_NPS:
            return {
                ...defaultOutput,
                topTitle: loginData?.slug?.toUpperCase(),
                // topTitle: t(`picker_360_title_workenv` as TKeys),
                hasProgress: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    disabled: true,
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        setState((s) => ({
                            ...s,
                            fsmState: nextState,
                        }))
                    },
                },
            }
        case FSM_STATES.NPS_360:
            return {
                ...defaultOutput,
                topTitle: t('nps_title'),
                hasProgress: false,
                bottomLeft: {
                    ...defaultOutput.bottomLeft,
                    disabled: true,
                },
                bottomRight: {
                    ...defaultOutput.bottomRight,
                    onClick: () => {
                        apiService
                            .sendScanningReport({
                                BeginDateHour: startedAt.toISOString(),
                                EndDateHour: new Date().toISOString(),
                                durationSeconds: diffSeconds(startedAt),
                                userAgent: navigator.userAgent,
                                language: langIndex2AppLang(langIndex),
                                //
                                IDUser: loginData.userId,
                                Email: loginData.userEmail,
                                JWTToken: loginData.token,
                                Slug: loginData.slug,
                                Source: loginData.source ?? '',
                                // TODO?
                                // From: loginData.from ?? '',
                                Language: langIndex2AppLang(
                                    state.params.assessmentParams[
                                        'langIndex'
                                    ]
                                ),
                                AnswerScore: scannedItems,
                            })
                            .then((success) => {
                                if (success) {
                                    setState((s) => ({
                                        ...s,
                                        fsmState: nextState,
                                    }))
                                } else {
                                    alert(
                                        t('title_error') +
                                            '\n\n' +
                                            t('hint_error')
                                    )
                                    setState((s) => ({
                                        ...s,
                                        fsmState: FSM_STATES.ERROR,
                                    }))
                                }
                            })
                            .catch((e) => {
                                alert(
                                    t('title_error') + '\n\n' + t('hint_error')
                                )
                                logException('sendScanningReport', 'useFSM', e)
                            })
                    },
                },
            }
        case FSM_STATES.THANKS_360:
            return {
                hasProgress: false,
                ...defaultOutput,
            }
        default:
            return {} as FSM_ACTIONS_TYPE
    }
}
